/**
 * Movers: deterministic motion specs for level primitives.
 *
 * A mover is a *pure function of time* — no state, no RNG, no integration. Every
 * client evaluates the same spec at (roughly) the same wall-clock second and
 * sees the same platform in the same place; nothing about motion ever travels
 * over the wire. The generator attaches a spec to a primitive's rest pose and
 * this module answers, for any time `t`:
 *
 * - where the primitive is ({@link applyMover}),
 * - how fast a surface point is moving ({@link moverVelocityAt}),
 * - whether it is solid at all ({@link moverActiveAt} — `blink` platforms
 *   spend part of every cycle vanished),
 * - and the conservative box its whole journey stays inside
 *   ({@link moverSweptAabb} — what the self-test checks invariants against).
 *
 * Pure data + math: no three.js, no DOM, importable from the self-tests and the
 * server alike. 1 unit = 1 metre, angles in radians, time in seconds.
 *
 * Conventions:
 * - A spec transforms the primitive's **base pose** (the fields the generator
 *   emitted). The base pose is the `slide` at zero offset, the `pendulum` at
 *   rest, and the `orbit` at angle `phase` when `t = 0`.
 * - `applyMover` writes into a *target* primitive of the same kind; the caller
 *   owns both copies. The base is never mutated.
 */

import type { Primitive, V3, V4 } from './primitives';

// ---------------------------------------------------------------------------
// Specs
// ---------------------------------------------------------------------------

/** Sinusoidal translation along a unit axis: sliders, elevators, ferries. */
export interface SlideMover {
  type: 'slide';
  /** Unit direction of travel. */
  axis: V3;
  /** Metres either side of the base pose. */
  amplitude: number;
  /** Seconds per full out-and-back cycle. */
  period: number;
  /** Phase offset, radians. Offset at time t is `A·sin(2πt/T + phase)`. */
  phase: number;
}

/** Constant-rate rotation about a pivot axis: carousels, sweeper bars. */
export interface OrbitMover {
  type: 'orbit';
  /** A point on the rotation axis, world space. */
  pivot: V3;
  /** Unit rotation axis. */
  axis: V3;
  /** Angular velocity, rad/s. Sign follows the right-hand rule about `axis`. */
  rate: number;
  /** Initial angle at `t = 0`, radians. */
  phase: number;
}

/** Sinusoidal rotation about a pivot axis: swinging logs and hammers. */
export interface PendulumMover {
  type: 'pendulum';
  /** A point on the swing axis, world space. */
  pivot: V3;
  /** Unit swing axis. */
  axis: V3;
  /** Swing half-angle, radians. */
  amplitude: number;
  /** Seconds per full swing cycle. */
  period: number;
  /** Phase offset, radians. Angle at time t is `A·sin(2πt/T + phase)`. */
  phase: number;
}

/** Periodic solid/vanished duty cycle: fading platforms. Geometry never moves. */
export interface BlinkMover {
  type: 'blink';
  /** Seconds per full cycle. */
  period: number;
  /** Fraction of the cycle spent solid, in (0, 1). */
  duty: number;
  /** Phase offset as a fraction of the period, in [0, 1). */
  phase: number;
}

export type MoverSpec = SlideMover | OrbitMover | PendulumMover | BlinkMover;

const TAU = Math.PI * 2;

// ---------------------------------------------------------------------------
// Decomposed transform at time t
// ---------------------------------------------------------------------------

/**
 * Rotation angle the spec applies at time `t`, about `spec.axis` through
 * `spec.pivot`. Zero for the non-rotating kinds.
 */
export function moverAngleAt(spec: MoverSpec, t: number): number {
  switch (spec.type) {
    case 'orbit':
      return spec.rate * t + spec.phase;
    case 'pendulum':
      return spec.amplitude * Math.sin((TAU / spec.period) * t + spec.phase);
    default:
      return 0;
  }
}

/** Translation the spec applies at time `t`. Zero for the rotating kinds. */
export function moverOffsetAt(spec: MoverSpec, t: number, out: V3): V3 {
  if (spec.type === 'slide') {
    const s = spec.amplitude * Math.sin((TAU / spec.period) * t + spec.phase);
    out[0] = spec.axis[0] * s;
    out[1] = spec.axis[1] * s;
    out[2] = spec.axis[2] * s;
  } else {
    out[0] = 0;
    out[1] = 0;
    out[2] = 0;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Pose
// ---------------------------------------------------------------------------

// Module scratch — this module is single-threaded and never re-enters.
const _off: V3 = [0, 0, 0];
const _q: V4 = [0, 0, 0, 1];

/** Quaternion for a rotation of `angle` about unit `axis`. */
function quatAxisAngle(axis: V3, angle: number, out: V4): V4 {
  const h = angle * 0.5;
  const s = Math.sin(h);
  out[0] = axis[0] * s;
  out[1] = axis[1] * s;
  out[2] = axis[2] * s;
  out[3] = Math.cos(h);
  return out;
}

/** Hamilton product `a ⊗ b` — apply `b` first, then `a`. Writes into `out`. */
function quatMul(a: V4, b: V4, out: V4): V4 {
  const [ax, ay, az, aw] = a;
  const [bx, by, bz, bw] = b;
  out[0] = aw * bx + ax * bw + ay * bz - az * by;
  out[1] = aw * by - ax * bz + ay * bw + az * bx;
  out[2] = aw * bz + ax * by - ay * bx + az * bw;
  out[3] = aw * bw - ax * bx - ay * by - az * bz;
  return out;
}

/** Rotate point `p` about `pivot` by quaternion `q`, in place. */
function rotateAbout(p: V3, pivot: V3, q: V4): void {
  const px = p[0] - pivot[0];
  const py = p[1] - pivot[1];
  const pz = p[2] - pivot[2];
  const [qx, qy, qz, qw] = q;
  // q * v * q⁻¹ via the optimised form.
  const ix = qw * px + qy * pz - qz * py;
  const iy = qw * py + qz * px - qx * pz;
  const iz = qw * pz + qx * py - qy * px;
  const iw = -qx * px - qy * py - qz * pz;
  p[0] = pivot[0] + ix * qw + iw * -qx + iy * -qz - iz * -qy;
  p[1] = pivot[1] + iy * qw + iw * -qy + iz * -qx - ix * -qz;
  p[2] = pivot[2] + iz * qw + iw * -qz + ix * -qy - iy * -qx;
}

/**
 * Write the pose of `base` at time `t` into `target`.
 *
 * `target` must be a primitive of the same kind as `base` (a deep clone —
 * see {@link clonePrimitive}); its geometric fields are overwritten, its
 * material and mover references are left alone. `base` is never touched, so
 * repeated calls cannot accumulate error.
 */
export function applyMover(base: Primitive, spec: MoverSpec, t: number, target: Primitive): void {
  if (spec.type === 'blink') return; // geometry never moves; solidity is separate
  if (spec.type === 'slide') {
    moverOffsetAt(spec, t, _off);
    switch (base.kind) {
      case 'box':
      case 'sphere': {
        const tgt = target as { center: V3 };
        const src = base as unknown as { center: V3 };
        tgt.center[0] = src.center[0] + _off[0];
        tgt.center[1] = src.center[1] + _off[1];
        tgt.center[2] = src.center[2] + _off[2];
        break;
      }
      case 'cylinder':
      case 'capsule': {
        const tgt = target as { a: V3; b: V3 };
        const src = base as unknown as { a: V3; b: V3 };
        for (let i = 0; i < 3; i++) {
          tgt.a[i] = (src.a[i] as number) + (_off[i] as number);
          tgt.b[i] = (src.b[i] as number) + (_off[i] as number);
        }
        break;
      }
    }
    return;
  }

  // Rotation about pivot/axis.
  const angle = moverAngleAt(spec, t);
  quatAxisAngle(spec.axis, angle, _q);
  switch (base.kind) {
    case 'box': {
      const tgt = target as typeof base;
      tgt.center[0] = base.center[0];
      tgt.center[1] = base.center[1];
      tgt.center[2] = base.center[2];
      rotateAbout(tgt.center, spec.pivot, _q);
      quatMul(_q, base.quat, tgt.quat);
      break;
    }
    case 'sphere': {
      const tgt = target as typeof base;
      tgt.center[0] = base.center[0];
      tgt.center[1] = base.center[1];
      tgt.center[2] = base.center[2];
      rotateAbout(tgt.center, spec.pivot, _q);
      break;
    }
    case 'cylinder':
    case 'capsule': {
      const tgt = target as { a: V3; b: V3 };
      const src = base as unknown as { a: V3; b: V3 };
      for (let i = 0; i < 3; i++) {
        tgt.a[i] = src.a[i] as number;
        tgt.b[i] = src.b[i] as number;
      }
      rotateAbout(tgt.a as V3, spec.pivot, _q);
      rotateAbout(tgt.b as V3, spec.pivot, _q);
      break;
    }
  }
}

/** Deep copy of a primitive's geometric fields (material is shared by reference). */
export function clonePrimitive(prim: Primitive): Primitive {
  switch (prim.kind) {
    case 'box':
      return {
        kind: 'box',
        center: [...prim.center],
        half: [...prim.half],
        quat: [...prim.quat],
        material: prim.material,
        ...(prim.mover !== undefined ? { mover: prim.mover } : {}),
      };
    case 'sphere':
      return {
        kind: 'sphere',
        center: [...prim.center],
        radius: prim.radius,
        material: prim.material,
        ...(prim.mover !== undefined ? { mover: prim.mover } : {}),
      };
    case 'cylinder':
    case 'capsule':
      return {
        kind: prim.kind,
        a: [...prim.a],
        b: [...prim.b],
        radius: prim.radius,
        material: prim.material,
        ...(prim.mover !== undefined ? { mover: prim.mover } : {}),
      };
  }
}

// ---------------------------------------------------------------------------
// Velocity
// ---------------------------------------------------------------------------

/**
 * World-space linear velocity of the surface point `point` at time `t`, m/s.
 *
 * For the rotating kinds this is `ω × (point − pivot)`; a `blink` never moves.
 * The physics uses this to bounce off movers in the *relative* frame and to
 * hand a rider the platform's momentum on launch.
 */
export function moverVelocityAt(spec: MoverSpec, t: number, point: V3, out: V3): V3 {
  switch (spec.type) {
    case 'slide': {
      const w = TAU / spec.period;
      const s = spec.amplitude * w * Math.cos(w * t + spec.phase);
      out[0] = spec.axis[0] * s;
      out[1] = spec.axis[1] * s;
      out[2] = spec.axis[2] * s;
      return out;
    }
    case 'orbit':
    case 'pendulum': {
      const rate =
        spec.type === 'orbit'
          ? spec.rate
          : spec.amplitude * (TAU / spec.period) * Math.cos((TAU / spec.period) * t + spec.phase);
      const wx = spec.axis[0] * rate;
      const wy = spec.axis[1] * rate;
      const wz = spec.axis[2] * rate;
      const rx = point[0] - spec.pivot[0];
      const ry = point[1] - spec.pivot[1];
      const rz = point[2] - spec.pivot[2];
      out[0] = wy * rz - wz * ry;
      out[1] = wz * rx - wx * rz;
      out[2] = wx * ry - wy * rx;
      return out;
    }
    default:
      out[0] = 0;
      out[1] = 0;
      out[2] = 0;
      return out;
  }
}

// ---------------------------------------------------------------------------
// Blink solidity
// ---------------------------------------------------------------------------

/** Where in its cycle a blink platform is right now. */
export interface BlinkState {
  /** Solid and collidable. */
  on: boolean;
  /** Seconds until the state next flips. */
  remaining: number;
}

/** State of a blink cycle at time `t`. Non-blink specs are always on. */
export function blinkStateAt(spec: MoverSpec, t: number, out: BlinkState): BlinkState {
  if (spec.type !== 'blink') {
    out.on = true;
    out.remaining = Infinity;
    return out;
  }
  const cycle = ((t / spec.period + spec.phase) % 1 + 1) % 1;
  if (cycle < spec.duty) {
    out.on = true;
    out.remaining = (spec.duty - cycle) * spec.period;
  } else {
    out.on = false;
    out.remaining = (1 - cycle) * spec.period;
  }
  return out;
}

const _blink: BlinkState = { on: true, remaining: Infinity };

/** True when the primitive is solid at time `t`. Everything but `blink` always is. */
export function moverActiveAt(spec: MoverSpec | undefined, t: number): boolean {
  if (spec === undefined || spec.type !== 'blink') return true;
  return blinkStateAt(spec, t, _blink).on;
}

// ---------------------------------------------------------------------------
// Swept bounds
// ---------------------------------------------------------------------------

/** Sample count for the swept-AABB estimate, and the pad that covers the gaps. */
const SWEEP_SAMPLES = 64;
const SWEEP_PAD = 0.2;

interface Aabb {
  min: V3;
  max: V3;
}

/** Axis-aligned bounds of one primitive at its current pose. */
function primAabb(prim: Primitive, out: Aabb): void {
  switch (prim.kind) {
    case 'box': {
      // extent_i = Σ_j |R_ij| · half_j, from the rotation matrix of the quat.
      const [x, y, z, w] = prim.quat;
      const [hx, hy, hz] = prim.half;
      const r00 = 1 - 2 * (y * y + z * z);
      const r01 = 2 * (x * y - z * w);
      const r02 = 2 * (x * z + y * w);
      const r10 = 2 * (x * y + z * w);
      const r11 = 1 - 2 * (x * x + z * z);
      const r12 = 2 * (y * z - x * w);
      const r20 = 2 * (x * z - y * w);
      const r21 = 2 * (y * z + x * w);
      const r22 = 1 - 2 * (x * x + y * y);
      const ex = Math.abs(r00) * hx + Math.abs(r01) * hy + Math.abs(r02) * hz;
      const ey = Math.abs(r10) * hx + Math.abs(r11) * hy + Math.abs(r12) * hz;
      const ez = Math.abs(r20) * hx + Math.abs(r21) * hy + Math.abs(r22) * hz;
      out.min[0] = prim.center[0] - ex;
      out.min[1] = prim.center[1] - ey;
      out.min[2] = prim.center[2] - ez;
      out.max[0] = prim.center[0] + ex;
      out.max[1] = prim.center[1] + ey;
      out.max[2] = prim.center[2] + ez;
      break;
    }
    case 'sphere':
      for (let i = 0; i < 3; i++) {
        out.min[i] = (prim.center[i] as number) - prim.radius;
        out.max[i] = (prim.center[i] as number) + prim.radius;
      }
      break;
    case 'cylinder':
    case 'capsule':
      for (let i = 0; i < 3; i++) {
        out.min[i] = Math.min(prim.a[i] as number, prim.b[i] as number) - prim.radius;
        out.max[i] = Math.max(prim.a[i] as number, prim.b[i] as number) + prim.radius;
      }
      break;
  }
}

/**
 * Conservative axis-aligned bounds containing the primitive over its whole
 * motion cycle: the union of sampled poses, padded to cover the gaps between
 * samples. What the self-test holds mover invariants against, and what the
 * broadphase registers a dynamic primitive under (a superset never misses).
 *
 * Allocates; build-time and boot-time only, never per frame.
 */
export function moverSweptAabb(base: Primitive, spec: MoverSpec): { min: V3; max: V3 } {
  const probe = clonePrimitive(base);
  const box: Aabb = { min: [0, 0, 0], max: [0, 0, 0] };
  const result: Aabb = {
    min: [Infinity, Infinity, Infinity],
    max: [-Infinity, -Infinity, -Infinity],
  };

  // One full cycle covers every pose: slide and pendulum are periodic in
  // `period`, an orbit is periodic in `2π/|rate|`, and a blink never moves.
  const cycle =
    spec.type === 'orbit'
      ? spec.rate !== 0
        ? TAU / Math.abs(spec.rate)
        : 0
      : spec.type === 'blink'
        ? 0
        : spec.period;

  const n = cycle === 0 ? 1 : SWEEP_SAMPLES;
  for (let i = 0; i < n; i++) {
    const t = cycle === 0 ? 0 : (i / n) * cycle;
    applyMover(base, spec, t, probe);
    primAabb(probe, box);
    for (let k = 0; k < 3; k++) {
      if ((box.min[k] as number) < (result.min[k] as number)) result.min[k] = box.min[k] as number;
      if ((box.max[k] as number) > (result.max[k] as number)) result.max[k] = box.max[k] as number;
    }
  }

  const pad = cycle === 0 ? 0 : SWEEP_PAD;
  for (let k = 0; k < 3; k++) {
    result.min[k] = (result.min[k] as number) - pad;
    result.max[k] = (result.max[k] as number) + pad;
  }
  return result;
}
